// ============================================================================
// AI INSIGHTS UTILITIES MODULE
// Helper functions for data processing and utilities
// ============================================================================

/**
 * Replaces double quotes with single quotes in all strings within an object
 * @param {Object} obj - Object to process
 */
export const replaceQuotes = (obj) => {
  for (const key in obj) {
    if (typeof obj[key] === 'string') {
      obj[key] = obj[key].replace(/"([^"]+)"/g, "'$1'");
    } else if (obj[key] && typeof obj[key] === 'object') {
      replaceQuotes(obj[key]);
    }
  }
};

/**
 * Sleep utility for delays
 * @param {number} ms - Milliseconds to sleep
 */
export const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

/**
 * Validates insights data against schema requirements
 * @param {Object} data - Insights data to validate
 * @returns {Object} - { valid: boolean, errors: string[] }
 */
export const validateInsightsData = (data) => {
  const errors = [];
  
  // Check required top-level fields
  const requiredFields = [
    'executive_summary',
    'key_discovery_rules',
    'attribute_insights',
    'likely_root_causes',
    'process_optimization_recommendations',
    'data_driven_nudges',
    'behavioral_adjustments',
    'summary_of_key_gains'
  ];
  
  requiredFields.forEach(field => {
    if (!data[field]) {
      errors.push(`Missing required field: ${field}`);
    }
  });
  
  // Validate executive summary
  if (data.executive_summary) {
    if (!data.executive_summary.overview) {
      errors.push('Executive summary missing overview');
    }
    if (!data.executive_summary.critical_issues) {
      errors.push('Executive summary missing critical_issues');
    }
    if (!data.executive_summary.process_health_score) {
      errors.push('Executive summary missing process_health_score');
    }
  }
  
  // Validate key discoveryRules
  if (data.key_discovery_rules) {
    if (!data.key_discovery_rules.signals) {
      errors.push('Key discoveryRules missing signals');
    }
    if (!data.key_discovery_rules.evidence) {
      errors.push('Key discoveryRules missing evidence');
    }
    if (!data.key_discovery_rules.interpretation) {
      errors.push('Key discoveryRules missing interpretation');
    }
  }
  
  // Validate root causes have confidence levels
  if (data.likely_root_causes && Array.isArray(data.likely_root_causes)) {
    data.likely_root_causes.forEach((cause, index) => {
      if (!cause.confidence_level) {
        errors.push(`Root cause ${index + 1} missing confidence_level`);
      }
      if (!cause.affected_scope) {
        errors.push(`Root cause ${index + 1} missing affected_scope`);
      }
    });
  }
  
  return {
    valid: errors.length === 0,
    errors
  };
};

/**
 * Extracts key metrics summary from insights data
 * @param {Object} data - Insights data
 * @returns {Object} - Summary metrics
 */
export const extractMetricsSummary = (data) => {
  const summary = {
    healthScore: data.executive_summary?.process_health_score || 0,
    criticalIssuesCount: data.executive_summary?.critical_issues?.length || 0,
    discovery_rules_count: data.key_discovery_rules?.evidence?.length || 0,
    rootCausesCount: data.likely_root_causes?.length || 0,
    recommendationsCount: data.process_optimization_recommendations?.length || 0,
    criticalRecommendations: 0,
    highRecommendations: 0,
    highConfidenceCauses: 0,
    mediumConfidenceCauses: 0,
    lowConfidenceCauses: 0
  };
  
  // Count recommendations by priority
  if (data.process_optimization_recommendations) {
    data.process_optimization_recommendations.forEach(rec => {
      if (rec.priority === 'Critical') summary.criticalRecommendations++;
      if (rec.priority === 'High') summary.highRecommendations++;
    });
  }
  
  // Count root causes by confidence
  if (data.likely_root_causes) {
    data.likely_root_causes.forEach(cause => {
      if (cause.confidence_level === 'High') summary.highConfidenceCauses++;
      if (cause.confidence_level === 'Medium') summary.mediumConfidenceCauses++;
      if (cause.confidence_level === 'Low') summary.lowConfidenceCauses++;
    });
  }
  
  return summary;
};

/**
 * Formats insights data for export (JSON)
 * @param {Object} data - Insights data
 * @param {string} modelTitle - Model title
 * @returns {string} - Formatted JSON string
 */
export const formatInsightsForExport = (data, modelTitle) => {
  const exportData = {
    metadata: {
      model: modelTitle,
      generatedAt: new Date().toISOString(),
      version: '2.0'
    },
    insights: data
  };
  
  return JSON.stringify(exportData, null, 2);
};

/**
 * Converts insights data to plain text summary
 * @param {Object} data - Insights data
 * @returns {string} - Plain text summary
 */
export const convertToPlainText = (data) => {
  let text = '';
  
  // Executive Summary
  text += '=== EXECUTIVE SUMMARY ===\n\n';
  text += `Health Score: ${data.executive_summary?.process_health_score || 'N/A'}/10\n\n`;
  text += `${data.executive_summary?.overview || ''}\n\n`;
  
  if (data.executive_summary?.critical_issues) {
    text += 'Critical Issues:\n';
    data.executive_summary.critical_issues.forEach((issue, i) => {
      text += `${i + 1}. ${issue}\n`;
    });
    text += '\n';
  }
  
  // Key Discovery Rules
  text += '=== KEY DISCOVERY RULES ===\n\n';
  if (data.key_discovery_rules?.signals) {
    text += 'Signals:\n';
    data.key_discovery_rules.signals.forEach((signal, i) => {
      text += `${i + 1}. ${signal}\n`;
    });
    text += '\n';
  }
  
  // Root Causes
  text += '=== ROOT CAUSE ANALYSIS ===\n\n';
  if (data.likely_root_causes) {
    data.likely_root_causes.forEach((cause, i) => {
      text += `${i + 1}. ${cause.symptom}\n`;
      text += `   Cause: ${cause.likely_cause}\n`;
      text += `   Confidence: ${cause.confidence_level}\n`;
      text += `   Scope: ${cause.affected_scope}\n\n`;
    });
  }
  
  // Recommendations
  text += '=== RECOMMENDATIONS ===\n\n';
  if (data.process_optimization_recommendations) {
    data.process_optimization_recommendations.forEach((rec, i) => {
      text += `${i + 1}. [${rec.priority}] ${rec.recommendation}\n`;
      text += `   Type: ${rec.change_type}\n`;
      text += `   Complexity: ${rec.implementation_complexity}\n\n`;
    });
  }
  
  return text;
};

/**
 * Gets health score color class
 * @param {number} score - Health score 1-10
 * @returns {string} - CSS class name
 */
export const getHealthScoreClass = (score) => {
  if (score >= 7) return 'health-good';
  if (score >= 4) return 'health-medium';
  return 'health-poor';
};

/**
 * Gets priority icon emoji
 * @param {string} priority - Priority level
 * @returns {string} - Emoji icon
 */
export const getPriorityIcon = (priority) => {
  const icons = {
    'Critical': '🔴',
    'High': '🟠',
    'Medium': '🟡',
    'Low': '🟢'
  };
  return icons[priority] || '⚪';
};

/**
 * Gets timeframe icon emoji
 * @param {string} timeframe - Timeframe string
 * @returns {string} - Emoji icon
 */
export const getTimeframeIcon = (timeframe) => {
  if (timeframe.includes('Quick Win')) return '⚡';
  if (timeframe.includes('Long-term')) return '🎯';
  return '🚀';
};
